export enum MetricType {
  HOURS_IN_VR = "hoursInVr",
  USER_SESSIONS = "userSessions",
  UNIQUE_ACTIVE_USERS = "uniqueActiveUsers",
  CERTIFICATIONS = "certifications",
}

export interface ExperienceUsageData {
  id: string;
  name: string;
  hoursInVr: number;
  userSessions: number;
  uniqueActiveUsers: number;
  certifications: number;
}

export interface ChartDatum {
  id: string;
  name: string;
  [MetricType.HOURS_IN_VR]?: number;
  [MetricType.USER_SESSIONS]?: number;
  [MetricType.UNIQUE_ACTIVE_USERS]?: number;
  [MetricType.CERTIFICATIONS]?: number;
  raw_hoursInVr: number;
  raw_userSessions: number;
  raw_uniqueActiveUsers: number;
  raw_certifications: number;
  fillHoursInVr: string;
  fillUserSessions: string;
  fillUniqueActiveUsers: string;
  fillCertifications: string;
}

export type ScaleMode = "linear" | "log";

export const MIN_DISPLAY_VALUE = 5;
export const LOG_RATIO_THRESHOLD = 100;

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

const truncateWithEllipsis = (
  value: string,
  maxLength: number,
  ellipsis = "...",
): string =>
  value.length > maxLength
    ? `${value.slice(0, maxLength - ellipsis.length)}${ellipsis}`
    : value;

export const formatExperienceName = (value: string): string[] => {
  const maxLineLength = 17;
  const ellipsis = "...";

  const trimmedValue = value.trim();
  if (trimmedValue.length <= maxLineLength) return [trimmedValue];

  const words = trimmedValue.split(/\s+/).filter(Boolean);

  if (words.length === 0) return [""];

  let firstLine = "";
  let splitIndex = words.length;

  for (let i = 0; i < words.length; i += 1) {
    const word = words[i]!;
    const candidate = firstLine ? `${firstLine} ${word}` : word;

    if (candidate.length > maxLineLength) {
      splitIndex = i;
      break;
    }

    firstLine = candidate;
  }

  // Edge case: first word itself is longer than maxLineLength
  // (or no spaces in the whole string)
  if (firstLine.length === 0) {
    const hardFirst = trimmedValue.slice(0, maxLineLength);
    const rest = trimmedValue.slice(maxLineLength).trim();

    if (!rest) return [hardFirst];

    return [hardFirst, truncateWithEllipsis(rest, maxLineLength, ellipsis)];
  }

  const remaining = words.slice(splitIndex).join(" ").trim();
  if (!remaining) return [firstLine];

  return [firstLine, truncateWithEllipsis(remaining, maxLineLength, ellipsis)];
};

export const formatYAxisValueCompact = (value: number): string => {
  if (value >= 1_000_000) {
    const digits = value % 1_000_000 === 0 ? 0 : 1;

    return `${(value / 1_000_000).toFixed(digits)}M`;
  }

  if (value >= 1000) {
    const digits = value % 1000 === 0 ? 0 : 1;

    return `${(value / 1000).toFixed(digits)}k`;
  }

  return String(Math.round(value));
};

export const isValueMasked = (
  value: number,
  isUserLevelDataEnabled = false,
): boolean => !isUserLevelDataEnabled && value < MIN_DISPLAY_VALUE;

export const getDisplayValue = (
  value: number,
  metric?: MetricType,
  isUserLevelDataEnabled = false,
): string => {
  if (isValueMasked(value, isUserLevelDataEnabled))
    return `<${MIN_DISPLAY_VALUE}`;

  const isHours = metric === MetricType.HOURS_IN_VR;
  const formatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: isHours ? 1 : 0,
    maximumFractionDigits: isHours ? 1 : 0,
  });

  return formatter.format(value);
};

const MINUTES_IN_HOUR = 60;

/**
 * Average session duration in minutes (total hours in VR / total user sessions).
 * Returns null when the value must not be shown: either input is masked as
 * `<5`, there are no sessions to divide by, or an input is not a finite number.
 */
export const getAvgSessionDurationMinutes = (
  hoursInVr: number,
  userSessions: number,
  isUserLevelDataEnabled = false,
): number | null => {
  if (!isFiniteNumber(hoursInVr) || !isFiniteNumber(userSessions)) return null;

  if (
    isValueMasked(hoursInVr, isUserLevelDataEnabled) ||
    isValueMasked(userSessions, isUserLevelDataEnabled)
  )
    return null;

  if (userSessions <= 0) return null;

  return (hoursInVr * MINUTES_IN_HOUR) / userSessions;
};

export const formatAvgSessionDuration = (minutes: number): string =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(minutes);

const pickNiceStep = (residual: number, magnitude: number): number => {
  if (residual <= 1.5) return magnitude;
  if (residual <= 3.5) return 2 * magnitude;
  if (residual <= 7.5) return 5 * magnitude;

  return 10 * magnitude;
};

export const buildLinearTicks = (
  min: number,
  max: number,
  tickCount?: number,
): number[] => {
  if (!isFiniteNumber(min) || !isFiniteNumber(max)) return [MIN_DISPLAY_VALUE];

  const effectiveMax = Math.max(max, min + 1);

  if (tickCount !== undefined && tickCount >= 2) {
    return Array.from(
      { length: tickCount },
      (_, i) => min + (i / (tickCount - 1)) * (effectiveMax - min),
    );
  }

  const rawStep = (effectiveMax - min) / 5;
  const safeRawStep = rawStep > 0 ? rawStep : 1;

  const magnitude = Math.pow(10, Math.floor(Math.log10(safeRawStep)));
  const residual = safeRawStep / magnitude;

  const niceStep = pickNiceStep(residual, magnitude);

  const ticks: number[] = [min];
  let currentTick = Math.ceil(min / niceStep) * niceStep;

  while (currentTick < effectiveMax) {
    if (currentTick > min) ticks.push(Math.round(currentTick));
    currentTick += niceStep;
  }

  // Force the exact max as the final tick
  ticks.push(effectiveMax);

  const unique = Array.from(new Set(ticks))
    .filter((v) => v >= min && v <= effectiveMax)
    .sort((a, b) => a - b);

  if (unique.length <= 8) return unique;

  const capped: number[] = [unique[0]!];
  const inner = unique.slice(1, -1);
  const stepIdx = Math.ceil(inner.length / 6);

  for (let i = 0; i < inner.length; i += stepIdx) {
    capped.push(inner[i]!);
  }

  capped.push(unique[unique.length - 1]!);

  return capped;
};

export const buildLogTicks = (
  min: number,
  max: number,
  tickCount?: number,
): number[] => {
  if (!isFiniteNumber(min) || !isFiniteNumber(max)) return [MIN_DISPLAY_VALUE];
  const effectiveMax = Math.max(max, min + 1);

  if (tickCount !== undefined && tickCount >= 2) {
    const logMin = Math.log10(Math.max(min, 1));
    const logMax = Math.log10(effectiveMax);

    return Array.from({ length: tickCount }, (_, i) =>
      Math.pow(10, logMin + (i / (tickCount - 1)) * (logMax - logMin)),
    );
  }

  const ticks: number[] = [min];

  let base = 10;
  while (base < effectiveMax) {
    ticks.push(base, base * 5);
    base *= 10;
  }

  ticks.push(effectiveMax);

  const unique = Array.from(
    new Set(ticks.filter((v) => v >= min && v <= effectiveMax)),
  ).sort((a, b) => a - b);

  if (unique[0] !== min) unique.unshift(min);
  if (unique[unique.length - 1] !== effectiveMax) unique.push(effectiveMax);

  if (unique.length <= 8) return unique;

  const capped: number[] = [unique[0]!];
  const inner = unique.slice(1, -1);
  const stepIdx = Math.ceil(inner.length / 6);

  for (let i = 0; i < inner.length; i += stepIdx) {
    capped.push(inner[i]!);
  }

  capped.push(unique[unique.length - 1]!);

  return capped;
};
